<script setup lang="ts">
import { useVuelidate } from '@vuelidate/core'
import { required, email, sameAs } from '@vuelidate/validators'

const isSending = ref(false)

const state = reactive({
  name: '',
  email: '',
  address: '',
  search: '',
  message: '',
  message2: '',
  checkbox: false,
  toggle: false
})

const rules = {
  name: { required },
  email: { required, email },
  address: { required },
  message: { required },
  message2: { required },
  checkbox: { sameAs: sameAs(true) },
  toggle: { sameAs: sameAs(true) }
}
const v$ = useVuelidate(rules, state)

async function submitForm() {
  isSending.value = true
  const isFormInvalid = await v$.value.$validate()
  if (!isFormInvalid) {
    return
  }

  useTimeoutFn(() => {
    isSending.value = false
  }, 4000)
}
</script>

<template>
  <form class="container py-20" @submit.prevent="submitForm">
    <div class="mb-5 mt-10 text-5xl font-medium">Forms</div>
    <div class="mb-5 mt-10 text-xl font-medium">Input label above (default)</div>
    <div class="grid-default">
      <div class="col-span-full lg:col-span-4">
        <FormInput
          v-model="state.name"
          placeholder="Full Name22"
          label="Full Name"
          name="name"
          :is-required="true"
          :is-error="v$.name.$error"
          :message="v$.name.$error && v$.name.$errors[0]?.$message"
          :is-message-visible="v$.name.$error"
          :message-fixed="true"
          @change="v$.name.$touch"
        />
      </div>
    </div>
    <div class="mb-5 mt-10 text-xl font-medium">Input label inside and icon</div>
    <div class="grid-default">
      <div class="col-span-full lg:col-span-4">
        <FormInput
          v-model="state.email"
          placeholder="Email"
          label="Email"
          name="email"
          :icon="{ name: 'custom:mail-outline' }"
          :is-required="true"
          :is-error="v$.email.$error"
          :message="v$.email.$error && v$.email.$errors[0]?.$message"
          :is-message-visible="v$.email.$error"
          :message-fixed="true"
          @change="v$.email.$touch"
        />
      </div>
    </div>
    <div class="mb-5 mt-10 text-xl font-medium">With placeholder and hidden label</div>
    <div class="grid-default">
      <div class="col-span-full lg:col-span-4">
        <FormInput
          v-model="state.address"
          placeholder="Address"
          label="Address"
          name="address"
          :is-label-visible="false"
          :is-required="true"
          :is-error="v$.address.$error"
          :message="v$.address.$error && v$.address.$errors[0]?.$message"
          :is-message-visible="v$.address.$error"
          :message-fixed="true"
          @change="v$.address.$touch"
        />
      </div>
    </div>
    <div class="mb-5 mt-10 text-xl font-medium">Search</div>
    <div class="grid-default">
      <div class="col-span-full lg:col-span-4">
        <FormInput
          v-model="state.search"
          placeholder="Search"
          label="Search"
          name="search"
          type="search"
          message="Please enter a search term"
          :is-message-visible="true"
          :is-label-visible="false"
          :icon="{ name: 'custom:search' }"
          :show-clear="true"
        />
      </div>
    </div>
    <div class="mb-5 mt-10 text-xl font-medium">Textarea</div>
    <div class="grid-default">
      <div class="col-span-full lg:col-span-4">
        <FormTextarea
          v-model="state.message"
          class="min-h-40"
          placeholder="Message"
          label="Message"
          name="message"
          :is-required="true"
          :is-error="v$.message.$error"
          :message="v$.message.$error && v$.message.$errors[0]?.$message"
          :is-message-visible="v$.message.$error"
          :message-fixed="true"
          @change="v$.message.$touch"
        />
      </div>
      <div class="col-span-full lg:col-span-4 lg:col-start-1">
        <FormTextarea
          v-model="state.message2"
          class="min-h-40"
          :label-inside="true"
          label="Message"
          name="message2"
          :is-required="true"
          :is-error="v$.message2.$error"
          :message="v$.message2.$error && v$.message2.$errors[0]?.$message"
          :is-message-visible="v$.message2.$error"
          :message-fixed="true"
          @change="v$.message2.$touch"
        />
      </div>
    </div>
    <div class="mb-5 mt-10 text-3xl font-medium">Checkbox</div>
    <div class="grid-default">
      <div class="col-span-full lg:col-span-4">
        <FormCheckbox
          v-model="state.checkbox"
          label="Default blue style"
          :is-required="true"
          :is-error="v$.checkbox.$error"
          @change="v$.checkbox.$touch"
        />
      </div>
    </div>
    <div class="mb-5 mt-10 text-3xl font-medium">Toggle</div>
    <div class="grid-default">
      <div class="col-span-full lg:col-span-4">
        <FormToggle
          v-model="state.toggle"
          name="surface-switch"
          label="Surface dark"
          :is-error="v$.toggle.$error"
          @change="v$.toggle.$touch"
        />
      </div>
    </div>
    <div class="grid-default">
      <div class="col-span-full lg:col-span-4">
        <div class="mt-12 text-center lg:text-right">
          <Button type="submit" label="Send message" />
        </div>
      </div>
    </div>
  </form>
</template>
